/**
 * Card for a sport club.
 */
import * as React from 'react';
import { Trophy, MapPin, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import type { SportClubPageActions } from './sport-club-configs';
import { deriveSportClubDisplay } from './sport-club-table-utils';
import type { SportClub } from '@/types/sport-club.types';

/** Props for {@link SportClubCard}. */
interface SportClubCardProps {
  club: SportClub;
  pageActions?: SportClubPageActions;
  onView?: (club: SportClub) => void;
  onEdit?: (club: SportClub) => void;
  onDelete?: (id: string) => void;
}

/** Card for a sport club. */
export const SportClubCard: React.FC<SportClubCardProps> = ({
  club,
  pageActions,
  onView,
  onEdit,
  onDelete,
}) => {
  const canView = pageActions?.canView ?? true;
  const canEdit = pageActions?.canEdit ?? true;
  const canDelete = pageActions?.canDelete ?? true;

  const { typeConfig, TypeIcon, websiteLabel } = deriveSportClubDisplay(club);

  return (
    <TableCard onClick={() => onView?.(club)}>
      <div className="data-card-header">
        <LabelBadge
          label={typeConfig.label}
          icon={TypeIcon}
          variant={typeConfig.badgeVariant}
          color={typeConfig.badgeColor}
          size="sm"
        />
      </div>

      <div className="data-card-content" style={{ overflow: 'hidden' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] shrink-0">
            <Trophy className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-foreground text-sm truncate">{club.name}</span>
            {club.description && (
              <span className="text-xs text-muted-foreground truncate">{club.description}</span>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          {club.city && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="w-3.5 h-3.5 shrink-0 text-[var(--color-1-nuance-1)]" />
              <span className="truncate">{club.city}</span>
            </div>
          )}

          {club.website && (
            <div className="flex items-center gap-2 text-xs">
              <Globe className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
              <a
                href={club.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-3-nuance-1)] hover:underline truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {websiteLabel}
              </a>
            </div>
          )}

          {club.contactEmail && (
            <div className="flex items-center gap-2 text-xs">
              <Mail className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
              <a
                href={`mailto:${club.contactEmail}`}
                className="text-foreground hover:text-[var(--color-3-nuance-1)] truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {club.contactEmail}
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          {canView && onView && (
            <Button
              preset="row-view"
              onClick={(e) => {
                e.stopPropagation();
                onView(club);
              }}
            />
          )}
          {canEdit && onEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(club);
              }}
            />
          )}
          {canDelete && onDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(club.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
